import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  ComparisonSheetState,
  PriceComparisonDto,
  TenderSupplier,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/ComparisonSheet.types';
import { UnlockPricingDialog } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/UnlockPricingDialog';
import { UnlockPricingFormValues } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/UnlockPricingDialog/UnlockPricingDialog';
import dayjs from 'dayjs';
import { TenderUnlockPricingRequest } from '@Types/api/form/tenderRequest/TenderUnlockPricingRequest';
import {
  useGetTenderTotalAmountQuery,
  useSaveTenderQuantityMutation,
  useUnlockTenderPricingMutation,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { FormikContextType } from 'formik';
import { VendorQuantitySplit } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/VendorQuantitySplit';
import {
  VendorQuantitySplitFormValues,
  VendorQuantitySplitProps,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/VendorQuantitySplit/VendorQuantitySplit';
import {
  ComparisonDataList,
  TenderSaveQuantityRequest,
} from '@Types/api/form/tenderRequest/TenderSaveQuantityRquest';
import { getQuantitySplit } from '@Features/MaterialRequest/requests/components/ComparisonSheet/utils';
import { VendorPopup } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/VendorPopup';

import { NotificationContext } from '@Context/NotificationContext';
import { MaterialVendorItem, Vendor } from '../types';

import VendorListMobile from './components/VendorListMobile/VendorListMobile';

import { MaterialsFooter, VendorFooter } from './components/FooterSection';
import { RequestListMobile } from './components';

import styles from './materials-tab.module.css';

interface MaterialsTabMobileProps {
  comparisonSheetSuppliers: TenderSupplier[];
  comparisonSheetOffers: PriceComparisonDto[];
  formik: FormikContextType<ComparisonSheetState>;
  handleReviewApprove: () => void;
  handleSelectMaterial: (material: MaterialVendorItem) => void;
}

const MaterialsTabMobile: React.FC<MaterialsTabMobileProps> = ({
  comparisonSheetOffers,
  comparisonSheetSuppliers,
  formik,
  handleSelectMaterial,
  handleReviewApprove,
}) => {
  const { errorNotification } = useContext(NotificationContext);
  const { tenderId } = useParams();
  const location = useLocation();
  const state = location?.state as MaterialVendorItem;
  const [materialsList, setMaterialsList] = useState<Array<MaterialVendorItem>>(
    []
  );
  const [unlockPricing] = useUnlockTenderPricingMutation();
  const [showUnlock, setShowUnlock] = useState<boolean>(false);
  const { data: totalAmountData } = useGetTenderTotalAmountQuery(tenderId!, {
    skip: !tenderId,
  });
  const [saveTenderQuantity] = useSaveTenderQuantityMutation();
  const [splitInfo, setSplitInfo] = useState<
    VendorQuantitySplitProps['info'] | null
  >(null);
  const [vendorDetails, setVendorDetails] = useState<TenderSupplier>();

  useLayoutEffect(() => {
    const tempResult = comparisonSheetOffers.map((offer) => {
      return {
        id: String(offer.matId),
        material: offer.material,
        vendors: comparisonSheetSuppliers.map((supply) => {
          const vendorOffer = offer.vendorOffers.find(
            (vendorOfferItem) => Number(vendorOfferItem.vendorId) === supply.id
          );
          return {
            title: supply.name,
            vendorItem: vendorOffer || [],
          };
        }) as Vendor[],
      };
    }) as MaterialVendorItem[];

    setMaterialsList(tempResult);
  }, [comparisonSheetOffers, comparisonSheetSuppliers]);

  const handleUnlockPricing = useCallback(
    (values: UnlockPricingFormValues): void => {
      if (tenderId) {
        const time = dayjs(values.expiryTime);
        const submitDate = dayjs(values.expiryDate)
          .hour(time.hour())
          .minute(time.minute())
          .second(time.second())
          .toISOString();
        const body: TenderUnlockPricingRequest = {
          discount: values.expectedAmount || 0,
          submitDate,
          tenderId: Number(tenderId),
          remarks: values.remarks || '',
        };
        unlockPricing(body).then(() => {
          setShowUnlock(false);
        });
      }
    },
    [tenderId, unlockPricing]
  );

  const handleSaveSplit = ({ offers }: VendorQuantitySplitFormValues): void => {
    const material = splitInfo?.material;
    const requiredQty = splitInfo?.material.requiredQty;
    const tenderItemId = splitInfo?.offers[0].offer.tenderItemId;

    if (material && tenderItemId && requiredQty) {
      const adjustedOffers = getQuantitySplit(requiredQty, offers);
      const dataList: Partial<ComparisonDataList> = {
        tenderItemId: Number(tenderItemId),
        matID: Number(material.matId),
        matCode: String(material.code),
        material: String(material.description),
        reqQty: requiredQty,
      };
      for (let i = 1; i <= 5; i += 1) {
        const vendorFromOffer = splitInfo?.offers.find(
          (off) => off.vendor.id === adjustedOffers[i - 1]?.vendorId
        );

        if (vendorFromOffer) {
          const updatedQuantity = adjustedOffers[i - 1].adjustedQty;

          dataList[`vendor${i}Id`] = vendorFromOffer.offer.vendorId;
          // updated quantity
          dataList[`vendor${i}Qty`] = updatedQuantity;
          dataList[`vendor${i}Rate`] = vendorFromOffer.offer.rate;
        } else {
          dataList[`vendor${i}Id`] = 0;
          dataList[`vendor${i}Qty`] = 0;
          dataList[`vendor${i}Rate`] = 0;
        }
      }
      const payload: TenderSaveQuantityRequest = {
        tenderId: Number(tenderId),
        comparisonDataList: [dataList as ComparisonDataList],
      };

      saveTenderQuantity(payload).then(() => {
        setSplitInfo(null);
      });
    }
  };

  const handleShowVendorDetails = (vendor: TenderSupplier): void => {
    setVendorDetails(vendor);
  };

  const uniqSelectionNumber = useMemo(() => {
    const uniq = formik.values.selectedOffersIDs?.length
      ? Array.from(
          new Map(
            formik.values.selectedOffersIDs.map(({ matId, vendorId }) => [
              matId,
              vendorId,
            ])
          ).keys()
        ).length
      : 0;
    return uniq;
  }, [formik.values.selectedOffersIDs]);

  const isShowErrorSplit = useMemo(() => {
    const allOffers = formik.values.allOffers?.length;
    if (uniqSelectionNumber === allOffers) {
      return false;
    }
    return true;
  }, [uniqSelectionNumber, formik.values.allOffers]);

  const onReviewApprove = (): void => {
    if (isShowErrorSplit) {
      errorNotification('Please select at least one vendor for all materials');
      return;
    }
    handleReviewApprove();
  };

  return (
    <div>
      {!state && (
        <RequestListMobile
          list={materialsList}
          onSelect={handleSelectMaterial}
          context="comparison-screen"
        />
      )}
      {state && (
        <VendorListMobile
          handleShowVendorDetails={handleShowVendorDetails}
          materialVendorItem={state}
          context="comparison-screen"
        />
      )}
      <div className={styles.footerSection}>
        {!state && (
          <MaterialsFooter
            onReviewApprove={onReviewApprove}
            totalAmount={totalAmountData?.finalTotAmount || 0}
            onSaveAsDraft={formik.handleSubmit}
            onUnlockPricing={() => {
              setShowUnlock(true);
            }}
          />
        )}
        {state && <VendorFooter onSplitQty={() => null} />}
      </div>
      {splitInfo && (
        <VendorQuantitySplit
          info={splitInfo}
          onSubmit={(res) => {
            handleSaveSplit(res);
          }}
          onCancel={() => {
            setSplitInfo(null);
          }}
        />
      )}
      {showUnlock && (
        <UnlockPricingDialog
          isOpen
          initialPricing={{
            expectedAmount: 1300,
            expiryDate: '',
            expiryTime: dayjs(),
            remarks: '',
          }}
          onCancel={() => {
            setShowUnlock(false);
          }}
          onSubmit={handleUnlockPricing}
        />
      )}
      {vendorDetails && (
        <VendorPopup
          vendorId={vendorDetails?.id}
          isOpened={Boolean(vendorDetails?.id)}
          title={vendorDetails?.name}
          onClose={() => {
            setVendorDetails(undefined);
          }}
        />
      )}
    </div>
  );
};

export default MaterialsTabMobile;
